// useVoiceDock — state machine behind the global voice-dock.
//
// Owns the dock view (hidden / expanded / collapsed) and the recorder, and
// derives the content phase from the recorder + the chat's activeRun. The dock
// drives the conversation through the shared useChatHistory instance, so the
// dock and the fullscreen chat screen are always one and the same run.

import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { RouterApi } from '../../router';
import type { UseChatHistory } from '../chat';
import { undoChange } from '../../api/endpoints/undo';
import { useDockRecorder } from './useDockRecorder';
import type { UseDockRecorder } from './useDockRecorder';
import { useSpeechRecognition } from './useSpeechRecognition';
import type { UseSpeechRecognition } from './useSpeechRecognition';
import { getVoiceEnginePref, resolveEngine } from './voiceEngine';
import type { VoiceEngineEffective } from './voiceEngine';

/** Whether the dock is shown, and how much of it. */
export type DockView = 'hidden' | 'expanded' | 'collapsed';

/** What the dock body currently shows. */
export type DockPhase = 'recording' | 'processing' | 'answer' | 'error';

/** Transient confirmation/error line shown after an inline action (undo). */
export interface DockNotice {
  readonly tone: 'ok' | 'err';
  readonly text: string;
}

export interface VoiceDockController {
  readonly view: DockView;
  readonly phase: DockPhase;
  readonly errorText: string | null;
  readonly recSeconds: number;
  readonly notice: DockNotice | null;
  readonly recorder: UseDockRecorder;
  readonly chat: UseChatHistory;
  /** Движок текущей записи: 'native' (Web Speech API) или 'whisper' (сервер). aidepo-64. */
  readonly engine: VoiceEngineEffective;
  /** Живой промежуточный текст при нативном распознавании ('' для whisper). */
  readonly interimText: string;
  /** Open the dock and start a fresh recording (the mic FAB entry point). */
  open(): void;
  /** Popover mic/stop button — stop & send while recording, else record. */
  toggleRecord(): void;
  collapse(): void;
  expand(): void;
  close(): void;
  /** Discard the current error/answer and record again. */
  reRecord(): void;
  /** Continue the dialogue by voice — a new run on the same conversation. */
  replyVoice(): void;
  /** Continue the dialogue by text. */
  replyText(text: string): void;
  /** Collapse the dock and open the same conversation fullscreen. */
  openInChat(): void;
  /** Reverse an AISummary action via its ChangeRecord id. */
  undo(changeId: string): Promise<void>;
}

export interface VoiceDockOptions {
  readonly chat: UseChatHistory;
  readonly router: RouterApi;
}

function computePhase(
  engine: VoiceEngineEffective,
  recorder: UseDockRecorder,
  native: UseSpeechRecognition,
  chat: UseChatHistory,
): DockPhase {
  if (recorder.error !== null || native.error !== null) return 'error';
  if (chat.activeRun?.stage === 'error') return 'error';
  if (engine === 'native') {
    if (native.state === 'listening') return 'recording';
    if (native.state === 'processing') return 'processing';
  } else {
    if (recorder.state === 'recording' || recorder.state === 'requesting') return 'recording';
    if (recorder.state === 'processing') return 'processing';
  }
  const stage = chat.activeRun?.stage;
  if (stage === 'recognizing' || stage === 'thinking') return 'processing';
  if (stage === 'done') return 'answer';
  return 'recording';
}

export function useVoiceDock({ chat, router }: VoiceDockOptions): VoiceDockController {
  const recorder = useDockRecorder();
  const native = useSpeechRecognition();
  const [view, setView] = useState<DockView>('hidden');
  const [recSeconds, setRecSeconds] = useState<number>(0);
  const [notice, setNotice] = useState<DockNotice | null>(null);
  // Движок, выбранный для текущей/следующей записи. Резолвится в startFresh из
  // настройки пользователя + фактической поддержки платформы (aidepo-64).
  const [engine, setEngine] = useState<VoiceEngineEffective>('whisper');

  // The recorder's stop() promise must outlive the click handler.
  const stopRef = useRef<UseDockRecorder['stop']>(recorder.stop);
  stopRef.current = recorder.stop;

  const phase = computePhase(engine, recorder, native, chat);
  const errorText = recorder.error ?? native.error ?? chat.activeRun?.error ?? null;

  // Активный захват: нативное прослушивание или серверная запись.
  const capturing =
    engine === 'native' ? native.state === 'listening' : recorder.state === 'recording';

  // Recording timer — ticks only while capture is live.
  useEffect(() => {
    if (!capturing) return;
    setRecSeconds(0);
    const started = Date.now();
    const timer = window.setInterval(() => {
      setRecSeconds(Math.floor((Date.now() - started) / 1000));
    }, 250);
    return () => window.clearInterval(timer);
  }, [capturing]);

  const startFresh = useCallback((): void => {
    setNotice(null);
    recorder.reset();
    native.reset();
    chat.clearRun();
    // Эффективный движок — из настройки пользователя + поддержки платформы.
    // 'native' недоступен (iOS-PWA / Firefox / офлайн) → тихо уходим в whisper.
    const eng = resolveEngine(getVoiceEnginePref());
    setEngine(eng);
    if (eng === 'native') {
      void native.start((transcript) => {
        void chat.sendVoiceNative(transcript);
      });
    } else {
      void recorder.start();
    }
  }, [recorder, native, chat]);

  const open = useCallback((): void => {
    setView('expanded');
    if (recorder.state === 'idle' && native.state === 'idle') startFresh();
  }, [recorder.state, native.state, startFresh]);

  const stopAndSend = useCallback((): void => {
    if (engine === 'native') {
      // native.stop() финализирует распознавание; onFinal → chat.sendVoiceNative.
      native.stop();
      return;
    }
    void stopRef.current().then((rec) => {
      if (rec !== null) void chat.sendVoice(rec.blob, rec.filename);
    });
  }, [engine, native, chat]);

  const toggleRecord = useCallback((): void => {
    if (capturing) stopAndSend();
    else startFresh();
  }, [capturing, stopAndSend, startFresh]);

  const collapse = useCallback((): void => setView('collapsed'), []);
  const expand = useCallback((): void => setView('expanded'), []);

  const close = useCallback((): void => {
    setView('hidden');
    if (recorder.state === 'recording') void stopRef.current();
    recorder.reset();
    native.reset();
    chat.clearRun();
    setNotice(null);
  }, [recorder, native, chat]);

  const replyVoice = useCallback((): void => startFresh(), [startFresh]);

  const replyText = useCallback(
    (text: string): void => {
      const trimmed = text.trim();
      if (trimmed.length === 0) return;
      setNotice(null);
      void chat.sendText(trimmed);
    },
    [chat],
  );

  const openInChat = useCallback((): void => {
    router.go('history');
    setView('collapsed');
  }, [router]);

  const undo = useCallback(
    async (changeId: string): Promise<void> => {
      try {
        await undoChange(changeId);
        setNotice({ tone: 'ok', text: 'Действие отменено' });
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setNotice({ tone: 'err', text: `Не удалось отменить: ${msg}` });
      }
    },
    [chat],
  );

  return {
    view,
    phase,
    errorText,
    recSeconds,
    notice,
    recorder,
    chat,
    engine,
    interimText: native.interim,
    open,
    toggleRecord,
    collapse,
    expand,
    close,
    reRecord: startFresh,
    replyVoice,
    replyText,
    openInChat,
    undo,
  };
}
